// src/interactiveUGC/components/CharacterSelect.js
import * as React from 'react';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import CharacterEditorDialog from './CharacterEditorDialog';
import { t } from '@lingui/macro';


const NARRATOR_ID = 'Narrator';
const ADD_SENTINEL = '__add__';

function dedupeById(list) {
  const seen = new Set(); const out = [];
  for (const it of list || []) {
    const k = String(it?.id ?? '');
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push({ id: String(it.id), name: it.name || it.id });
  }
  return out;
}

/**
 * Props:
 * - options: [{id, name}]
 * - value: string ('Narrator' or id)
 * - onChange: (id) => void
 * - onCreateCharacter: async (payload) => { id, name, ... }
 * - size, label, disabled
 */
export default function CharacterSelect({
  options,
  value,
  onChange,
  onCreateCharacter,
  size = 'small',
  label = t`Speaker`,
  disabled = false,
}) {
  const [openCreate, setOpenCreate] = React.useState(false);

  const merged = React.useMemo(() => dedupeById(options || []), [options]);
  const narrator = { id: NARRATOR_ID, name: t`Narrator` };

  // keep stable even if options refresh a tick later
  const valueInList =
    value === NARRATOR_ID || merged.some((o) => String(o.id) === String(value));
  const effectiveValue = valueInList ? value : NARRATOR_ID;

  const handleSelect = (e) => {
    const val = e.target.value;
    if (val === ADD_SENTINEL) {
      // Close the menu, then open the dialog so the menu doesn't overlay it.
      setTimeout(() => setOpenCreate(true), 0);
      return;
    }
    onChange?.(val);
  };

  const handleSaveNew = async (payload) => {
    try {
      const created = await onCreateCharacter?.(payload);
      if (created?.id) onChange?.(String(created.id)); // select immediately
    } finally {
      setOpenCreate(false);
    }
  };

  return (
    <>
      <TextField
        select
        size={size}
        label={label}
        disabled={disabled}
        margin="dense"
        value={effectiveValue}
        onChange={handleSelect}
        slotProps={{
          select: {
            MenuProps: {
              disablePortal: false, // portal to <body> to avoid nested-dialog focus churn
            },
          },
        }}
        sx={{ minWidth: 0 }}
      >
        <MenuItem value={NARRATOR_ID}>{narrator.name}</MenuItem>

        {merged.map((opt) => (
          <MenuItem key={opt.id} value={opt.id}>
            {opt.name}
          </MenuItem>
        ))}

        {/* Styled like your red outlined "Add" buttons */}
        <MenuItem
          value={ADD_SENTINEL}
          disableRipple
          sx={{
            mt: 0.75,
            mx: 1,
            mb: 0.25,
            border: (t) => `1px solid ${t.palette.error.main}`,
            color: 'error.main',
            borderRadius: 1,
            justifyContent: 'center',
            fontWeight: 600,
            transition: (t) =>
              t.transitions.create(['background-color', 'border-color'], {
                duration: t.transitions.duration.shortest,
              }),
            '&:hover': {
              borderColor: 'error.dark',
              backgroundColor: (t) =>
                t.palette.mode === 'dark'
                  ? t.palette.error.dark + '22'
                  : t.palette.error.main + '14',
            },
            '&.Mui-focusVisible': {
              borderColor: 'error.dark',
              backgroundColor: (t) =>
                t.palette.mode === 'dark'
                  ? t.palette.error.dark + '22'
                  : t.palette.error.main + '14',
            },
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <AddIcon fontSize="small" />
            <Typography variant="body2">{t`Add new character…`}</Typography>
          </Stack>
        </MenuItem>
      </TextField>

      <CharacterEditorDialog
        open={openCreate}
        initialValue={{ name: '' }}
        onClose={() => setOpenCreate(false)}
        onSave={handleSaveNew}
      />
    </>
  );
}
